const GAP_SEVERITIES = [
  {
    min: 0,
    max: 15,
    label: 'Low',
    background: '#ecfdf5',
    color: '#047857',
    className: 'gap--low',
  },
  {
    min: 16,
    max: 35,
    label: 'Moderate',
    background: '#fef9c3',
    color: '#a16207',
    className: 'gap--moderate',
  },
  {
    min: 36,
    max: 55,
    label: 'High',
    background: '#ffedd5',
    color: '#c2410c',
    className: 'gap--high',
  },
  {
    min: 56,
    max: Infinity,
    label: 'Extreme',
    background: '#fee2e2',
    color: '#b91c1c',
    className: 'gap--extreme',
  },
]

const UNKNOWN_GAP_SEVERITY = {
  label: 'No data',
  background: '#f3f4f6',
  color: '#4b5563',
  className: 'gap--unknown',
}

const PERSPECTIVE_LABELS = {
  left: 'Left-Leaning',
  left_wing: 'Left-Leaning',
  leaning_left_wing: 'Left-Leaning',
  center: 'Center',
  right: 'Right-Leaning',
  right_wing: 'Right-Leaning',
  leaning_right_wing: 'Right-Leaning',
}

function toFiniteNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export function getGapSeverity(gap) {
  const normalizedGap = toFiniteNumber(gap)
  if (normalizedGap === null || normalizedGap < 0) {
    return UNKNOWN_GAP_SEVERITY
  }

  return GAP_SEVERITIES.find(({ min, max }) => normalizedGap >= min && normalizedGap <= max)
    || UNKNOWN_GAP_SEVERITY
}

export function getSentimentEmoji(score) {
  const normalizedScore = toFiniteNumber(score)
  if (normalizedScore === null) return '😐'

  if (normalizedScore <= 30) return '😠'
  if (normalizedScore <= 55) return '😐'
  if (normalizedScore <= 75) return '🙂'
  return '😊'
}

export function getPerspectiveLabel(key) {
  if (key == null) return 'Unknown Perspective'

  const normalizedKey = String(key).trim().toLowerCase().replace(/-/g, '_')
  return PERSPECTIVE_LABELS[normalizedKey] || 'Unknown Perspective'
}

export const SENTIMENT_TOKENS = {
  positive: {
    label: 'Positive Tone',
    icon: 'fa-arrow-trend-up',
    color: '#166534',
    bg: '#f0fdf4',
    border: 'rgba(22, 101, 52, 0.2)',
  },
  neutral: {
    label: 'Balanced',
    icon: 'fa-minus',
    color: '#475569',
    bg: '#f8fafc',
    border: 'rgba(71, 85, 105, 0.2)',
  },
  negative: {
    label: 'Critical Tone',
    icon: 'fa-arrow-trend-down',
    color: '#92400e',
    bg: '#fffbeb',
    border: 'rgba(146, 64, 14, 0.2)',
  },
}

import {
  FRAMING_LEAN_TOKENS,
  getFramingLeanToken,
  hasFramingFields,
  resolveFramingSide,
} from '@/utils/framingHelpers'

/**
 * Legacy exact-label keys (dev mocks / older clients) plus broadSide keys.
 * Prefer getFramingLeanToken / article framing fields for V6.
 * Do NOT map categorySpecified (source cohort) through this helper as framing.
 */
export const LEAN_TOKENS = {
  'Left-Leaning': {
    color: '#3730a3',
    bg: '#eef2ff',
    border: 'rgba(55, 48, 163, 0.18)',
  },
  Center: {
    color: '#374151',
    bg: '#f9fafb',
    border: 'rgba(55, 65, 81, 0.18)',
  },
  'Right-Leaning': {
    color: '#881337',
    bg: '#fff1f2',
    border: 'rgba(136, 19, 55, 0.18)',
  },
  // broadSide-keyed tokens (article framing only)
  left: FRAMING_LEAN_TOKENS.left,
  center: FRAMING_LEAN_TOKENS.center,
  right: FRAMING_LEAN_TOKENS.right,
  insufficient: FRAMING_LEAN_TOKENS.insufficient,
}

export function getSentimentToken(score) {
  if (score == null) return null
  const s = Number(score)
  if (!Number.isFinite(s)) return null
  if (s >= 67) return SENTIMENT_TOKENS.positive
  if (s >= 34) return SENTIMENT_TOKENS.neutral
  return SENTIMENT_TOKENS.negative
}

/**
 * Resolve a lean chip token.
 * Accepts: broadSide string, legacy mock labels, or a prediction/article object
 * with framing fields. Does not invent a side from categorySpecified.
 */
export function getLeanToken(labelOrPrediction) {
  if (labelOrPrediction == null) return null
  if (typeof labelOrPrediction === 'object') {
    if (hasFramingFields(labelOrPrediction)) {
      return getFramingLeanToken(labelOrPrediction)
    }
    // Object without framing fields — do not treat as framing
    return null
  }
  const asBroad = resolveFramingSide({ broadSide: labelOrPrediction })
  if (asBroad && FRAMING_LEAN_TOKENS[asBroad]) {
    return FRAMING_LEAN_TOKENS[asBroad]
  }
  return LEAN_TOKENS[labelOrPrediction] ?? null
}

export { getFramingLeanToken, hasFramingFields }
